import React, { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { AlertTriangle, Download, Play, RefreshCw } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { libraryReturnPath } from '../utils/libraryNavigation';
import apiClient from '../services/apiClient';
import MarketCoverImage from './MarketCoverImage';
import LibraryReviews from './LibraryReviews';
import MarketListingForm from './MarketListingForm';
import NoteDetailHeader from './NoteDetailHeader';
import NotePageHeader from './NotePageHeader';
import NoteCardList from './NoteCardList';
import QuickPracticeAction from './QuickPracticeAction';
import FavoriteNoteAction from './FavoriteNoteAction';
import { normalizeNote, noteText } from '../utils/notePresentation';
import { getNoteThemeStyle, normalizeNoteTheme } from '../utils/noteTheme';
import usePageThemeColor from '../hooks/usePageThemeColor';
import MarketOwnerActions from './MarketOwnerActions';
import { useConfirmDialog } from './ConfirmDialog';
import { t } from '../i18n';
import { editorText } from './noteEditorText';
import './MarketPage.css';

const REPORT_CATEGORIES = ['inappropriate', 'legal', 'misleading', 'harmful', 'other'];

function MarketDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = libraryReturnPath(location.state, '/market');
  const { listingId } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [report, setReport] = useState({ category: 'inappropriate', details: '' });
  const [reported, setReported] = useState(false);
  const editorRef = React.useRef(null);
  const importOperation = React.useRef(null);
  const { confirm, confirmDialog } = useConfirmDialog();
  usePageThemeColor(editing ? null : normalizeNoteTheme(listing || {}));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setListing(null);
    setError('');
    setEditing(false);
    setReporting(false);
    setReported(false);
    importOperation.current = null;
    apiClient.getLibraryMarketListing(listingId)
      .then(data => { if (!cancelled) setListing(data); })
      .catch(requestError => { if (!cancelled) setError(requestError.message || t('market.errors.detail')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [listingId]);

  const run = async (action) => {
    setBusy(true); setError('');
    try { await action(); }
    catch (requestError) { setError(requestError.message || t('market.errors.action')); }
    finally { setBusy(false); }
  };

  const mergeOwnerListing = (updated) => {
    setListing((current) => ({
      ...current,
      ...updated,
      isOwner: true,
      imported: false,
      canReview: false,
    }));
  };

  const handlePublicationToggle = () => run(async () => {
    const status = listing.status === 'active' ? 'withdrawn' : 'active';
    mergeOwnerListing(await apiClient.updateLibraryMarketListing(listing.id, { status }));
  });

  const handleDeleteListing = async () => {
    const accepted = await confirm({
      title: t('market.deleteConfirmTitle'),
      message: t('market.deleteConfirmMessage', undefined, { title: listing.title }),
      confirmLabel: t('market.delete'),
      cancelLabel: t('common.cancel'),
      tone: 'danger',
    });
    if (!accepted) return;
    await run(async () => {
      await apiClient.deleteLibraryMarketListing(listing.id);
      navigate(returnTo, { replace: true });
    });
  };

  if (loading) return <div className="market-state">{t('market.loading')}</div>;
  if (!listing) return <div className="market-state is-error"><p>{error || t('market.errors.notFound')}</p><button onClick={() => navigate(returnTo)}>{t('market.back')}</button></div>;

  const note = normalizeNote(listing, 'market');
  const noteThemeStyle = getNoteThemeStyle(note);
  const imported = Boolean(listing.imported || listing.installedNoteId);
  const importLabel = noteText(imported ? 'added' : 'addToNotes');

  const handleImport = () => {
    if (imported || busy) return;
    run(async () => {
      try {
        importOperation.current ||= uuid();
        const result = await apiClient.importLibraryMarketListing(listing.id, ...(listing.studyKind === 'vocabulary' ? [{ operationId: importOperation.current }] : []));
        navigate(listing.studyKind === 'vocabulary' ? '/vocabulary' : '/notes', { state: { importedNote: { id: result.noteId, title: listing.title } } });
        setListing(current => ({
          ...current,
          imported: true,
          canReview: !current.isOwner,
          installedNoteId: result.noteId || null,
        }));
      } catch (err) {
        if (err.status !== 409) throw err;
        setListing(current => ({
          ...current,
          imported: true,
          canReview: !current.isOwner,
          installedNoteId: err.data?.error?.installedNoteId || null,
        }));
      }
    });
  };

  if (editing) return <div className="note-composer-page">
    <NotePageHeader title={editorText('edit')} description={editorText('editHelp')}
      onBack={() => editorRef.current ? editorRef.current.requestCancel() : setEditing(false)} />
    <MarketListingForm ref={editorRef} listing={listing} onCancel={() => setEditing(false)} onSaved={() => setEditing(false)} onSave={async (form) => {
      mergeOwnerListing(await apiClient.updateLibraryMarketListing(listing.id, form));
    }} />
  </div>;

  return (
    <div className={`note-detail-page${noteThemeStyle ? ' has-note-theme' : ''}`} style={noteThemeStyle}>
      <NoteDetailHeader note={note} backLabel={t('dashboard.library')} onBack={() => navigate(returnTo)}
        cover={<MarketCoverImage listingId={listing.id} hasCover={Boolean(listing.coverUrl)} coverVersion={listing.coverVersion} alt={listing.title} />}
        additionalFacts={<>
          <span>{`★ ${Number(listing.averageRating || 0).toFixed(1)} (${Number(listing.ratingCount || 0)})`}</span>
          <span>{`↓ ${Number(listing.uniqueImportCount || 0)}`}</span>
        </>}
        actions={listing.isOwner ? (
          <MarketOwnerActions
            status={listing.status}
            busy={busy}
            onEdit={() => setEditing((value) => !value)}
            onTogglePublication={handlePublicationToggle}
            onDelete={handleDeleteListing}
          />
        ) : (
          <MarketOwnerActions busy={busy} onReport={() => setReporting((value) => !value)} />
        )}
        primaryActionClassName="has-learning-actions"
        primaryAction={<div className="note-detail-learning-actions">
          <FavoriteNoteAction sourceType="market" sourceId={listing.id} initialFavorite={Boolean(listing.isFavorite)} />
          {listing.studyKind !== 'vocabulary' && <QuickPracticeAction source={{ type: 'market', id: listing.id }} note={note} disabled={!note.cards.length}
            renderTrigger={(open, quickBusy) => <button type="button" className="note-detail-button note-detail-button-primary" disabled={quickBusy || !note.cards.length} onClick={open}>
              <Play size={18} fill="currentColor" aria-hidden="true" />{noteText('quickPractice')}
            </button>} />}
          <button type="button" className="note-detail-button note-detail-round-action" aria-label={importLabel} title={importLabel}
            disabled={busy || imported} onClick={handleImport}>
            <Download size={20} aria-hidden="true" />
          </button>
        </div>}
      >
          {error && <p className="market-detail-error" role="alert">{error}</p>}

          {listing.isOwner && listing.sourceNoteId && <div className="note-detail-cta"><button type="button" className="note-detail-button" onClick={() => navigate(`/notes/${encodeURIComponent(listing.sourceNoteId)}/publish`, { state: { listingId: listing.id, ...(listing.studyKind === 'vocabulary' ? { studyKind: 'vocabulary' } : {}) } })}><RefreshCw size={17} />{noteText('reviewSource')}</button></div>}
          {reporting && !reported && (
            <form className="market-report-form" onSubmit={(event) => { event.preventDefault(); run(async () => { await apiClient.reportLibraryMarketListing(listing.id, report); setReported(true); setReporting(false); }); }}>
              <strong><AlertTriangle size={18} />{t('market.reportTitle')}</strong>
              <select value={report.category} onChange={(event) => setReport((current) => ({ ...current, category: event.target.value }))}>
                {REPORT_CATEGORIES.map((category) => <option key={category} value={category}>{t(`market.reportCategories.${category}`)}</option>)}
              </select>
              <input maxLength={300} value={report.details} onChange={(event) => setReport((current) => ({ ...current, details: event.target.value.replace(/[\r\n]/g, ' ') }))} placeholder={t('market.reportDetails')} />
              <button type="submit" disabled={busy}>{t('market.submitReport')}</button>
            </form>
          )}
          {reported && <p className="market-report-sent">{t('market.reportSent')}</p>}
      </NoteDetailHeader>

      <NoteCardList cards={note.cards} preview title={t('market.examples')} description={t('market.examplesHelp')} paginationLabel={t('market.previewPagination')} />
      <LibraryReviews
        initialData={listing.reviews}
        canReview={Boolean(listing.canReview)}
        loadReviews={(page) => apiClient.getLibraryMarketReviews(listing.id, { page })}
        saveReview={(review) => apiClient.saveLibraryMarketReview(listing.id, review)}
      />
      {confirmDialog}
    </div>
  );
}

export default MarketDetailsPage;
